import { AddCircleOutlined, RemoveCircleOutlined } from "@mui/icons-material";
import { Popover, Box, Typography, Slider, Backdrop, DialogContent, Button, DialogActions, DialogTitle, IconButton, Stack, useTheme } from "@mui/material";
import { useEffect, useState, type JSX } from "react";
import {isMobile, isTablet} from 'react-device-detect';
import { Mark } from "../../../common/models/Mark";

export type FormFeetAndInchesPickerPopupCloseReason = "ok" | "backdropClick" | "escapeKeyDown";

export type FormFeetAndInchesPickerPopupProps = {
    label?: string | JSX.Element;
    totalInches: number,
    maxFeet: number;
    onChange: (totalInches: number | null) => void;
    anchorEl: HTMLElement | null;
    open: boolean;
    onClose: (reason: FormFeetAndInchesPickerPopupCloseReason) => void;
    error?: boolean;
    helperText?: string;
    hideClear?: boolean;
};

export function FormFeetAndInchesPickerPopup({
    totalInches,
    label,
    maxFeet,
    onChange,
    anchorEl,
    open,
    onClose,
    error,
    helperText,
    hideClear
}: FormFeetAndInchesPickerPopupProps) {
    const [feet, setFeet] = useState<number | null>(0);
    const [inches, setInches] = useState<number | null>(0);
    //const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
    const isPhone = isMobile && !isTablet;
    const theme = useTheme();

    useEffect(() => {
        if (Number.isFinite(+totalInches)) {
            setFeet(Math.floor(+totalInches / 12));
            setInches(+totalInches % 12);
        } else {
            setFeet(null);
            setInches(null);
        }
    }, [totalInches]);

    const feetList: Mark[] = [];
    const increment = 4 + ((maxFeet % 4) ? 1 : 0);
    for (let i: number = 0; i <= maxFeet; i += increment) {
        feetList.push({
            label: `${i}'`,
            value: i
        });
    }

    const inchesList: Mark[] = [];
    for (let i: number = 0; i <= 12; i += 3) {
        inchesList.push({
            label: `${i.toFixed(2)}"`,
            value: i
        });
    }

    function getFeetValueText(value: number) {
        return `${value}'`;
    }

    function getInchesValueText(value: number) {
        return `${value.toFixed(2)}"`;
    }

    function onClear() {
        setFeet(null);
        setInches(null);
        onChange(null);
    }


    return (
        <>
            {isPhone &&
                <Backdrop open={open} />
            }
            <Popover
                open={open}
                onClose={(_event, reason) => onClose(reason)}
                anchorEl={!isPhone ? anchorEl : undefined}
                anchorOrigin={!isPhone
                    ? {
                        vertical: "bottom",
                        horizontal: "center",
                    }
                    : {
                        vertical: "center",
                        horizontal: "center",
                    }
                }
                transformOrigin={!isPhone
                    ? {
                        vertical: "top",
                        horizontal: "center",
                    }
                    : {
                        vertical: "center",
                        horizontal: "center",
                    }
                }
                slotProps={{
                    paper: {
                        sx: {
                            maxWidth: "400px",
                            width: theme => `calc(100% - ${theme.spacing(8)})`
                        }
                    }
                }}
            >
                <DialogTitle>
                    {label}
                </DialogTitle>

                <DialogContent>
                    <Box sx={{ marginLeft: 1, marginRight: 1 }}>
                        <Typography id="feet-label" gutterBottom variant="subtitle2">
                            {`Feet: ${Number.isFinite(feet) ? getFeetValueText(feet as number) : ""}`}
                        </Typography>

                        <Stack direction='row' sx={{
                            justifyContent: 'space-between'
                        }}>

                            <IconButton 
                                style={{
                                fontSize: '1rem',
                                marginTop: isPhone ? '2px' : 0,
                                paddingTop: isPhone ? undefined : '4px',
                                marginBottom: 'auto',
                                marginRight: theme.spacing(2)
                                
                                }}
                                onClick={() => {
                                    if (feet! > 0) {
                                        const newFeet = feet! -1;
                                        setFeet(newFeet);
                                        const totalInches = (newFeet as number ?? 0) * 12 + (inches ?? 0);
                                        onChange(totalInches);
    
                                    }
                                }}
                            >
                                <RemoveCircleOutlined />
                            </IconButton>                        

                        <Slider
                            key="feetSlider"
                            name="feet"
                            value={feet??0}
                            getAriaValueText={getFeetValueText}
                            aria-labelledby="feet-label"
                            step={1}
                            marks={feetList}
                            valueLabelDisplay="off"
                            min={0}
                            max={maxFeet}
                            onChange={(_event: Event, value: number | number[]) => {
                                setFeet(value as number);
                                // TODO: Should this be in useEffect? Doesn't seem to be causing any problems.
                                const totalInches = (value as number ?? 0) * 12 + (inches ?? 0);
                                onChange(totalInches);
                            }}
                        />

                        <IconButton 
                            style={{
                            fontSize: '1rem',
                            marginTop: isPhone ? '2px' : 0,
                            paddingTop: isPhone ? undefined : '4px',
                            marginBottom: 'auto',
                            marginLeft: theme.spacing(2)
                            }}
                            onClick={() => {
                                if (feet! < maxFeet) {
                                    const newFeet = feet! + 1;
                                    setFeet(newFeet);
                                    const totalInches = (newFeet as number ?? 0) * 12 + (inches ?? 0);
                                    onChange(totalInches);
                                }

                            }}
                        >
                            <AddCircleOutlined />
                        </IconButton>
                        </Stack>
                    </Box>

                    <Box sx={{ marginLeft: 1, marginRight: 1 }}>
                        <Typography id="inches-label" gutterBottom variant="subtitle2">
                            {`Inches: ${Number.isFinite(inches) ? getInchesValueText(inches as number) : ""}`}
                        </Typography>

                        <Stack direction='row' sx={{
                            justifyContent: 'space-between'
                        }}>

                            <IconButton 
                                style={{
                                fontSize: '1rem',
                                marginTop: isPhone ? '2px' : 0,
                                paddingTop: isPhone ? undefined : '4px',
                                marginBottom: 'auto',
                                marginRight: theme.spacing(2)
                                
                                }}
                                onClick={() => {
                                    if (inches! > 0) {
                                        const newInches = inches! - .25;
                                        setInches(newInches);
                                        const totalInches = (feet ?? 0) * 12 + (newInches as number ?? 0);
                                        onChange(totalInches);
                                    }
                                }}
                            >
                                <RemoveCircleOutlined />
                            </IconButton>                                           
                            <Slider
                                key="inchesSlider"
                                value={inches??0}
                                getAriaValueText={getInchesValueText}
                                aria-labelledby="inches-label"
                                step={.25}
                                marks={inchesList}
                                valueLabelDisplay="off"
                                min={0}
                                max={11.75}
                                onChange={(_event: Event, value: number | number[]) => {
                                    setInches(value as number);
                                    // TODO: Should this be in useEffect? Doesn't seem to be causing any problems.
                                    const totalInches = (feet ?? 0) * 12 + (value as number ?? 0);
                                    onChange(totalInches);
                                }}
                            />

                            <IconButton 
                                style={{
                                fontSize: '1rem',
                                marginTop: isPhone ? '2px' : 0,
                                paddingTop: isPhone ? undefined : '4px',
                                marginBottom: 'auto',
                                marginLeft: theme.spacing(2)
                                }}
                                onClick={() => {
                                    if (inches! < 11.75) {
                                        const newInches = inches! + .25;
                                        setInches(newInches);
                                        const totalInches = (feet ?? 0) * 12 + (newInches as number ?? 0);
                                        onChange(totalInches);
   
                                    }

                                }}
                            >
                                <AddCircleOutlined/>
                            </IconButton>
                        </Stack>                            
                    </Box>
                    <Typography variant="body2" sx={{
                        color: error
                            ? "error.main"
                            : "inherit"
                    }}>
                        {helperText}
                    </Typography>
                </DialogContent>


                <DialogActions>
                    {!hideClear && 
                    <Button onClick={() => onClear()} >
                        Clear
                    </Button>
                    }
                    <Button onClick={() => onClose("ok")} >
                        OK
                    </Button>
                </DialogActions>

            </Popover>
        </>
    );
}
